import { formatTaskDisplayName } from "./taskDisplayName";
import { formatDeletedUserName } from "./teamBoardCardFilters";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function stripHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+\n/g, "\n")
    .trim();
}

function formatDisplayDate(value) {
  if (!value) return "—";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "0000-00-00 00:00:00") return "—";
    const mysqlRegex = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/;
    if (mysqlRegex.test(trimmed)) {
      const normalized = trimmed.replace("T", " ");
      const [datePart, timePart] = normalized.split(" ");
      const [year, month, day] = datePart.split("-");
      return `${day}/${month}/${year} ${String(timePart).slice(0, 5)}`;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR");
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return "";
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function buildDetailRows(entries) {
  return entries
    .filter(([, value]) => value != null && String(value).trim() !== "" && String(value).trim() !== "—")
    .map(
      ([label, value]) =>
        `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`
    )
    .join("");
}

function looksLikeImageUrl(url) {
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(String(url || ""));
}

function isImageAttachment(att) {
  const mime = String(att?.mimeType || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  return looksLikeImageUrl(att?.fileName || att?.fileUrl);
}

function extractImageUrls(text) {
  const raw = String(text || "");
  return raw.match(/https?:\/\/[^\s<>"']+\.(?:png|jpe?g|gif|webp|bmp|svg)(?:\?[^\s<>"']*)?/gi) || [];
}

function renderImagesHtml(urls, alt) {
  return (urls || [])
    .filter((url) => url && String(url).trim())
    .map(
      (url) =>
        `<figure class="print-image"><img src="${escapeHtml(String(url).trim())}" alt="${escapeHtml(
          alt || "Imagem"
        )}" loading="eager" /></figure>`
    )
    .join("");
}

function assigneeLabel(assignee) {
  if (assignee == null) return "";
  if (typeof assignee === "object") {
    return formatDeletedUserName(
      assignee.nome || assignee.name || assignee.userName || String(assignee.id || ""),
      assignee.deleted,
      "excluído"
    );
  }
  return String(assignee);
}

function buildDetailsSection({ card, board, column, assignees, labels }) {
  const assigneeNames = (assignees || [])
    .map(assigneeLabel)
    .filter((name) => name && String(name).trim());

  const labelNames = (card?.labels || [])
    .map((l) => l?.name)
    .filter(Boolean)
    .join(", ");

  const rows = buildDetailRows([
    [labels.board || "Painel", board?.name],
    [labels.column || "Coluna", column?.name || card?.columnName],
    [
      labels.status || "Status",
      column?.isDone || card?.completedAt
        ? labels.done || "Concluída"
        : labels.open || "Em andamento",
    ],
    [labels.dueAt || "Prazo", card?.dueAt ? formatDisplayDate(card.dueAt) : ""],
    [labels.estimatedDuration || "Tempo necessário", card?.estimatedDuration],
    [labels.assignees || "Responsáveis", assigneeNames.join(", ")],
    [
      labels.required || "Obrigatória",
      card?.isRequired ? labels.yes || "Sim" : labels.no || "Não",
    ],
    [labels.labelsField || "Etiquetas", labelNames],
    [labels.client || "Cliente", card?.clientName],
    [
      labels.request || "Solicitação",
      card?.leadId ? `#${card.leadId}` : "",
    ],
    [labels.createdAt || "Criada em", card?.createdAt ? formatDisplayDate(card.createdAt) : ""],
    [
      labels.completedAt || "Concluída em",
      card?.completedAt ? formatDisplayDate(card.completedAt) : "",
    ],
  ]);

  if (!rows) {
    return `<p class="empty">${escapeHtml(labels.noDetails || "Sem detalhes da tarefa.")}</p>`;
  }

  return `<table class="details">${rows}</table>`;
}

function buildDescriptionSection(description, labels) {
  const text = stripHtml(description);
  const imagesHtml = renderImagesHtml(extractImageUrls(description), labels.imageAlt || "Imagem da descrição");
  if (!text && !imagesHtml) {
    return `<p class="empty">${escapeHtml(labels.noDescription || "Sem descrição.")}</p>`;
  }
  const textHtml = text ? `<div class="body">${escapeHtml(text).replace(/\n/g, "<br/>")}</div>` : "";
  return `${textHtml}${imagesHtml}`;
}

function buildChecklistsSection(checklists, labels) {
  const list = Array.isArray(checklists) ? checklists : [];
  if (!list.length) return "";

  const blocks = list
    .map((checklist) => {
      const items = Array.isArray(checklist?.items) ? checklist.items : [];
      const done = items.filter((item) => item?.isDone).length;
      const itemHtml = items.length
        ? items
            .map((item) => {
              const mark = item?.isDone ? "☑" : "☐";
              return `<li class="${item?.isDone ? "done" : ""}">${escapeHtml(mark)} ${escapeHtml(
                item?.title || "—"
              )}</li>`;
            })
            .join("")
        : `<li class="empty">${escapeHtml(labels.noChecklistItems || "Nenhum item.")}</li>`;

      return `
        <article class="checklist">
          <header>
            <strong>${escapeHtml(checklist?.title || labels.checklist || "Checklist")}</strong>
            <span>${done}/${items.length}</span>
          </header>
          <ul>${itemHtml}</ul>
        </article>
      `;
    })
    .join("");

  return `
    <h2>${escapeHtml(labels.checklistsSection || "Checklists")}</h2>
    <div class="checklists">${blocks}</div>
  `;
}

function linkedTaskLine(link, labels) {
  const status = link?.done || link?.completedAt || link?.columnIsDone
    ? labels.done || "Concluída"
    : link?.columnName || labels.open || "Em andamento";
  const board = link?.boardName ? ` · ${link.boardName}` : "";
  return `${formatTaskDisplayName(link?.id, link?.title) || "—"}${board} (${status})`;
}

function buildLinkedTasksSection(card, labels) {
  const parent = card?.parent || null;
  const children = Array.isArray(card?.children) ? card.children : [];
  if (!parent && !children.length) return "";

  const rows = [];
  if (parent) {
    rows.push([labels.linkedParent || "Origem", linkedTaskLine(parent, labels)]);
  }
  children.forEach((child, index) => {
    rows.push([
      children.length === 1
        ? labels.linkedChild || "Escalonada"
        : `${labels.linkedChild || "Escalonada"} ${index + 1}`,
      linkedTaskLine(child, labels),
    ]);
  });

  return `
    <h2>${escapeHtml(labels.linkedSection || "Escalonamentos")}</h2>
    <table class="details">${buildDetailRows(rows)}</table>
  `;
}

function buildAttachmentsSection(attachments, labels) {
  const list = Array.isArray(attachments) ? attachments : [];
  if (!list.length) {
    return `
      <h2>${escapeHtml(labels.attachmentsSection || "Anexos")}</h2>
      <p class="empty">${escapeHtml(labels.noAttachments || "Nenhum anexo.")}</p>
    `;
  }

  const items = list
    .map((att) => {
      const name = att?.fileName || labels.unnamedFile || "Arquivo";
      const url = att?.fileUrl || "";
      const size = formatFileSize(att?.sizeBytes);
      const meta = [size, url].filter(Boolean).join(" — ");
      const imageHtml = isImageAttachment(att) && url
        ? renderImagesHtml([url], name)
        : "";
      const title = url
        ? `<a href="${escapeHtml(url)}">${escapeHtml(name)}</a>`
        : escapeHtml(name);

      return `
        <article class="attachment">
          <div class="attachment-name">${title}</div>
          ${meta ? `<div class="meta">${escapeHtml(meta)}</div>` : ""}
          ${imageHtml}
        </article>
      `;
    })
    .join("");

  return `
    <h2>${escapeHtml(labels.attachmentsSection || "Anexos")}</h2>
    <div class="attachments">${items}</div>
  `;
}

function commentBodyHtml(comment, labels) {
  const displayText = stripHtml(comment?.displayBody != null ? comment.displayBody : comment?.body || "");
  const hasMedia = Boolean(
    comment?.imageUrl || comment?.videoUrl || comment?.audioUrl || comment?.fileUrl
  );
  const looksLikeFileName =
    hasMedia &&
    displayText &&
    /\.(png|jpe?g|gif|webp|bmp|svg|pdf|docx?|xlsx?|pptx?|txt|zip|rar|mp3|mp4|mov|webm|m4a|wav)$/i.test(
      displayText
    ) &&
    (!comment?.fileName || displayText === comment.fileName);
  const text = looksLikeFileName ? "" : displayText;
  const parts = [];
  if (text) parts.push(escapeHtml(text).replace(/\n/g, "<br/>"));

  const imageUrls = [];
  if (comment?.imageUrl) imageUrls.push(comment.imageUrl);
  if (!hasMedia) imageUrls.push(...extractImageUrls(comment?.body));
  const uniqueImages = [...new Set(imageUrls.filter(Boolean))];
  if (uniqueImages.length) {
    parts.push(renderImagesHtml(uniqueImages, labels.commentImageAlt || "Imagem do comentário"));
  }

  if (comment?.videoUrl) {
    parts.push(
      `<div class="meta">[${escapeHtml(labels.video || "Vídeo")}: <a href="${escapeHtml(
        comment.videoUrl
      )}">${escapeHtml(comment.videoUrl)}</a>]</div>`
    );
  }
  if (comment?.audioUrl) {
    parts.push(
      `<div class="meta">[${escapeHtml(labels.audio || "Áudio")}: <a href="${escapeHtml(
        comment.audioUrl
      )}">${escapeHtml(comment.audioUrl)}</a>]</div>`
    );
  }

  const fileUrl = comment?.fileUrl;
  if (fileUrl && !uniqueImages.includes(fileUrl) && fileUrl !== comment?.imageUrl) {
    const fileName = comment?.fileName || labels.unnamedFile || "Arquivo";
    parts.push(
      `<div class="meta">[${escapeHtml(labels.file || "Arquivo")}: <a href="${escapeHtml(
        fileUrl
      )}">${escapeHtml(fileName)}</a>]</div>`
    );
  }

  if (!parts.length) return escapeHtml("—");
  return parts.join("");
}

function buildCommentsSection(comments, labels) {
  const sorted = [...(comments || [])].sort((a, b) => {
    const ta = new Date(a?.createdAt || 0).getTime();
    const tb = new Date(b?.createdAt || 0).getTime();
    if (ta !== tb) return ta - tb;
    return (Number(a?.id) || 0) - (Number(b?.id) || 0);
  });

  if (!sorted.length) {
    return `<p class="empty">${escapeHtml(labels.noComments || "Nenhum comentário.")}</p>`;
  }

  const items = sorted
    .map((comment) => {
      const isLog = String(comment?.type || "").toLowerCase() === "log";
      const author = formatDeletedUserName(
        comment?.authorName || labels.unknownAuthor || "Usuário",
        comment?.authorDeleted,
        "excluído"
      );
      const when = formatDisplayDate(comment?.createdAt);
      const edited = comment?.editedAt
        ? ` <span class="edited">(${escapeHtml(labels.edited || "editado")})</span>`
        : "";

      if (isLog) {
        return `
          <article class="comment message-log">
            <header>
              <strong>${escapeHtml(labels.systemLog || "Registro")}</strong>
              <span>${escapeHtml(when)}</span>
            </header>
            <div class="body">${escapeHtml(comment?.body || "—")}${
              comment?.authorName ? ` · ${escapeHtml(comment.authorName)}` : ""
            }</div>
          </article>
        `;
      }

      return `
        <article class="comment">
          <header>
            <strong>${escapeHtml(author)}</strong>
            <span>${escapeHtml(when)}${edited}</span>
          </header>
          <div class="body">${commentBodyHtml(comment, labels)}</div>
        </article>
      `;
    })
    .join("");

  return `<div class="comments">${items}</div>`;
}

function buildPrintHtml({
  card,
  board,
  column,
  assignees,
  labels,
  logoUrl,
  printedBy,
  copyright,
}) {
  const title = labels.title || "Tarefa";
  const taskTitle = formatTaskDisplayName(card?.id, card?.title);
  const printedAt = formatDisplayDate(new Date());
  const printedByName = printedBy || labels.unknownUser || "—";
  const logoBlock = logoUrl
    ? `<img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(labels.brandName || "Xpro")}" class="logo" />`
    : `<div class="logo-fallback">${escapeHtml(labels.brandName || "Xpro")}</div>`;

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}${taskTitle ? ` — ${escapeHtml(taskTitle)}` : ""}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: "Segoe UI", Arial, sans-serif;
      color: #111;
      margin: 24px;
      font-size: 13px;
      line-height: 1.45;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
    }
    .print-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      border-bottom: 2px solid #e0e0e0;
      padding-bottom: 14px;
      margin-bottom: 18px;
    }
    .print-header .logo {
      height: 42px;
      width: auto;
      max-width: 180px;
      object-fit: contain;
    }
    .logo-fallback {
      font-size: 22px;
      font-weight: 700;
      letter-spacing: 0.02em;
    }
    .print-header-meta {
      text-align: right;
      font-size: 12px;
      color: #555;
      line-height: 1.5;
    }
    .print-header-meta strong { color: #111; }
    .print-content { flex: 1; }
    h1 { font-size: 20px; margin: 0 0 16px; }
    h2 {
      font-size: 15px;
      margin: 20px 0 8px;
      padding-bottom: 4px;
      border-bottom: 1px solid #ddd;
    }
    table.details {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 8px;
    }
    table.details th,
    table.details td {
      border: 1px solid #ddd;
      padding: 6px 8px;
      vertical-align: top;
      text-align: left;
    }
    table.details th {
      width: 180px;
      background: #f5f5f5;
      font-weight: 600;
    }
    .body { white-space: pre-wrap; word-break: break-word; }
    .checklists, .attachments, .comments {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .checklist, .attachment, .comment {
      border: 1px solid #ddd;
      border-radius: 6px;
      padding: 8px 10px;
      page-break-inside: avoid;
    }
    .checklist header, .comment header {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 6px;
      font-size: 12px;
    }
    .checklist header span, .comment header span { color: #666; white-space: nowrap; }
    .checklist ul {
      margin: 0;
      padding-left: 0;
      list-style: none;
    }
    .checklist li { margin: 3px 0; }
    .checklist li.done { color: #555; }
    .attachment-name { font-weight: 600; }
    .attachment-name a { color: #1565c0; text-decoration: none; }
    .meta { color: #666; font-size: 11px; margin-top: 2px; }
    .edited { color: #888; }
    .message-log {
      background: #f3f3f3;
      border-style: dashed;
      font-style: italic;
      color: #555;
    }
    .print-image {
      margin: 8px 0 0;
      padding: 0;
    }
    .print-image img {
      display: block;
      max-width: 100%;
      max-height: 420px;
      width: auto;
      height: auto;
      object-fit: contain;
      border: 1px solid #ddd;
      border-radius: 4px;
      background: #fafafa;
    }
    .empty { color: #666; font-style: italic; }
    .print-footer {
      margin-top: 32px;
      padding-top: 12px;
      border-top: 1px solid #ddd;
      text-align: center;
      color: #666;
      font-size: 11px;
    }
    @media print {
      body { margin: 12mm; }
      .print-footer {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 8mm 12mm;
        background: #fff;
      }
    }
  </style>
</head>
<body>
  <header class="print-header">
    ${logoBlock}
    <div class="print-header-meta">
      <div><strong>${escapeHtml(labels.printedBy || "Impresso por")}:</strong> ${escapeHtml(printedByName)}</div>
      <div><strong>${escapeHtml(labels.printedAt || "Impresso em")}:</strong> ${escapeHtml(printedAt)}</div>
    </div>
  </header>

  <main class="print-content">
    <h1>${escapeHtml(title)}${taskTitle ? ` — ${escapeHtml(taskTitle)}` : ""}</h1>

    <h2>${escapeHtml(labels.detailsSection || "Detalhes")}</h2>
    ${buildDetailsSection({ card, board, column, assignees, labels })}

    <h2>${escapeHtml(labels.descriptionSection || "Descrição")}</h2>
    ${buildDescriptionSection(card?.description, labels)}

    ${buildChecklistsSection(card?.checklists, labels)}
    ${buildLinkedTasksSection(card, labels)}
    ${buildAttachmentsSection(card?.attachments, labels)}

    <h2>${escapeHtml(labels.commentsSection || "Comentários")}</h2>
    ${buildCommentsSection(card?.comments, labels)}
  </main>

  <footer class="print-footer">
    ${escapeHtml(copyright || `© ${new Date().getFullYear()} Xpro. Todos os direitos reservados.`)}
  </footer>
</body>
</html>`;
}

function waitForImages(win) {
  const images = Array.from(win.document?.images || []);
  if (!images.length) return Promise.resolve();
  return Promise.all(
    images.map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            const done = () => resolve();
            img.addEventListener("load", done, { once: true });
            img.addEventListener("error", done, { once: true });
          })
    )
  );
}

/**
 * Abre uma janela formatada para impressão da tarefa (textos, imagens, comentários).
 */
export function printTaskCard({
  card,
  board = null,
  column = null,
  assignees = [],
  labels = {},
  logoUrl = "",
  printedBy = "",
  copyright = "",
}) {
  if (!card) return false;

  const html = buildPrintHtml({
    card,
    board,
    column,
    assignees,
    labels,
    logoUrl,
    printedBy,
    copyright,
  });

  const blobUrl = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
  const printWindow = window.open(blobUrl, "_blank");

  if (!printWindow) {
    URL.revokeObjectURL(blobUrl);
    window.alert(labels.popupBlocked || "Permita pop-ups para imprimir a tarefa.");
    return false;
  }

  printWindow.opener = null;

  const triggerPrint = async () => {
    try {
      await Promise.race([
        waitForImages(printWindow),
        new Promise((resolve) => setTimeout(resolve, 8000)),
      ]);
      printWindow.focus();
      printWindow.print();
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  };

  if (printWindow.document?.readyState === "complete") {
    setTimeout(triggerPrint, 100);
  } else {
    printWindow.onload = () => setTimeout(triggerPrint, 100);
  }

  return true;
}
